import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SText } from 'src/components/SText';
import { StepsIndicator } from 'src/components/StepsIndicator';
import { AdvisorCatalogLoader } from 'src/containers/advisorCatalogLoader';
import { CreateClientFormContainer } from '../../containers';
import {
  eStoreAgenciesForSelectSelector,
  eStoreAgencyInfoSelector,
  eStoreFormSenderSelector,
  eStoreFormToAgencySelector,
  eStoreSenderIsNotReceiverSelector,
  setEStoreSender,
  setEStoreSenderIsNotReceiver,
} from '../store';

type Props = {
  parentUrl: string;
};

export const EStoreSender = ({ parentUrl }: Props) => {
  const history = useHistory();

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const toAgencySelector = useSelector(eStoreFormToAgencySelector);
  const senderForm = useSelector(eStoreFormSenderSelector);
  const senderIsNotReceiver = useSelector(eStoreSenderIsNotReceiverSelector);

  const agencies = useSelector(eStoreAgenciesForSelectSelector);

  const currentAgency = useSelector(eStoreAgencyInfoSelector);

  const onlyAgenciesOutCurrent = useMemo(() => {
    if (!currentAgency) {
      return agencies;
    }

    return agencies.filter((agency) => agency.value !== currentAgency.slug);
  }, [currentAgency, agencies]);

  const onSubmit = useCallback(
    (data) => {
      if (senderIsNotReceiver) {
        history.push(`${parentUrl}/receiver`);
      } else {
        history.push(`${parentUrl}/details`);
      }
    },
    [senderIsNotReceiver, history, parentUrl],
  );

  const onFieldsUpdate = useCallback(
    (data) => {
      dispatch(setEStoreSender({ sender: data }));
    },
    [dispatch],
  );

  const onPreview = useCallback(() => {
    history.goBack();
  }, [history]);

  const onSenderIsNotReceiverCheckBoxChange = useCallback(() => {
    dispatch(setEStoreSenderIsNotReceiver({ senderIsNotReceiver: !senderIsNotReceiver }));
  }, [dispatch, senderIsNotReceiver]);

  return (
    <div className="h-screen py-3 md:h-auto md:mt-0 mt-6 w-full relative">
      <StepsIndicator totalSteps={senderIsNotReceiver ? 4 : 3} active={0} />
      <SText label="estore-sender.page-title" classNames="my-3 text-primary block text-lg text-center font-semibold" />
      <CreateClientFormContainer
        agencies={onlyAgenciesOutCurrent}
        requiredToAgency
        onSubmit={onSubmit}
        onFieldsUpdate={onFieldsUpdate}
        defaultValues={{ toAgency: toAgencySelector, ...senderForm }}
        onPreview={onPreview}
      >
        <div className="mt-6">
          <label className="cursor-pointer inline-block text-gray-600 font-regular" htmlFor="senderIsNotReceiver">
            <input
              className="mr-2 inline-block"
              type="checkbox"
              value="senderIsNotReceiver"
              id="senderIsNotReceiver"
              checked={senderIsNotReceiver}
              onChange={onSenderIsNotReceiverCheckBoxChange}
            />
            {t('estore-sender.sender-is-not-receiver')}
          </label>
        </div>

        <div>
          <AdvisorCatalogLoader zone="EStoreUnderDescription" testID="AdvisorCatalogLoader" />
        </div>
      </CreateClientFormContainer>
    </div>
  );
};
